import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../lib/components/custom/Icon";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "../../../lib/components/ui/avatar";
import { Badge } from "../../../lib/components/ui/badge";
import { Button } from "../../../lib/components/ui/button";
import { Input } from "../../../lib/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../lib/components/ui/select";
import { Skeleton } from "../../../lib/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../lib/components/ui/table";
import { useApi } from "../../../lib/hooks/api/use-api";
import AdminLayout from "../layout";

export default function AdminUsers() {
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");

	const { users } = useApi();

	// Fetch users data
	const { data: usersData, isLoading, refetch } = users.getAll();

	const filteredUsers = useMemo(() => {
		if (!usersData?.users) return [];

		return usersData.users.filter((user) => {
			const matchesSearch =
				user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
				user.phone?.toLowerCase().includes(searchTerm.toLowerCase());

			const matchesStatus =
				statusFilter === "all" ||
				(statusFilter === "active" && user.isActive) ||
				(statusFilter === "inactive" && !user.isActive);

			return matchesSearch && matchesStatus;
		});
	}, [usersData, searchTerm, statusFilter]);

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const getInitials = (name: string) => {
		return name
			.split(" ")
			.map((word) => word.charAt(0))
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	if (isLoading) {
		return (
			<AdminLayout>
				<div className="space-y-6">
					<div className="flex items-center justify-between">
						<Skeleton className="h-8 w-32" />
						<Skeleton className="h-10 w-24" />
					</div>
					<div className="bg-white border rounded-lg p-6">
						<div className="flex gap-4">
							<Skeleton className="h-10 flex-1" />
							<Skeleton className="h-10 w-32" />
						</div>
					</div>
					<div className="bg-white border rounded-lg overflow-hidden">
						<div className="p-8">
							<div className="space-y-4">
								{Array.from({ length: 8 }).map((_, i) => (
									<div key={i} className="flex items-center space-x-4">
										<Skeleton className="h-12 w-12 rounded-full" />
										<Skeleton className="h-4 w-32" />
										<Skeleton className="h-4 w-48" />
										<Skeleton className="h-4 w-24" />
										<Skeleton className="h-4 w-16" />
										<Skeleton className="h-4 w-20" />
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<h1 className="text-2xl font-bold">{t("admin.users.title")}</h1>
					<div className="text-sm text-muted-foreground">
						{t("admin.users.count", {
							filtered: filteredUsers.length,
							total: usersData?.totalUsers || 0,
						})}
					</div>
				</div>

				{/* Filters */}
				<div className="bg-white border rounded-lg p-6">
					<div className="flex flex-col sm:flex-row gap-4 items-center">
						<div className="flex-1">
							<div className="relative">
								<Icon
									name="Search"
									className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4"
								/>
								<Input
									placeholder={t("admin.users.search_placeholder")}
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="pl-10"
								/>
							</div>
						</div>

						<Select value={statusFilter} onValueChange={setStatusFilter}>
							<SelectTrigger className="w-full sm:w-40">
								<SelectValue placeholder={t("admin.users.status")} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">
									{t("admin.users.all_users")}
								</SelectItem>
								<SelectItem value="active">
									{t("admin.users.active")}
								</SelectItem>
								<SelectItem value="inactive">
									{t("admin.users.inactive")}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>

				{/* Users Table */}
				<div className="bg-white border rounded-lg overflow-hidden">
					{isLoading ? (
						<div className="p-8">
							<div className="space-y-4">
								{Array.from({ length: 8 }).map((_, i) => (
									<div key={i} className="flex items-center space-x-4">
										<Skeleton className="h-12 w-12 rounded-full" />
										<Skeleton className="h-4 w-32" />
										<Skeleton className="h-4 w-48" />
										<Skeleton className="h-4 w-24" />
										<Skeleton className="h-4 w-16" />
										<Skeleton className="h-4 w-20" />
									</div>
								))}
							</div>
						</div>
					) : filteredUsers.length === 0 ? (
						<div className="p-12 text-center">
							<Icon
								name="Users"
								className="w-16 h-16 text-muted-foreground mx-auto mb-4"
							/>
							<h3 className="text-lg font-semibold mb-2">
								{t("admin.users.no_users_found")}
							</h3>
							<p className="text-muted-foreground">
								{searchTerm || statusFilter !== "all"
									? t("admin.users.no_users_filtered")
									: t("admin.users.no_users_registered")}
							</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow className="bg-gray-50">
									<TableHead className="w-16 font-semibold">
										{t("admin.users.avatar")}
									</TableHead>
									<TableHead className="font-semibold">
										{t("admin.users.name")}
									</TableHead>
									<TableHead className="font-semibold">
										{t("admin.users.email")}
									</TableHead>
									<TableHead className="w-24 font-semibold">
										{t("admin.users.phone")}
									</TableHead>
									<TableHead className="w-20 font-semibold">
										{t("admin.users.status")}
									</TableHead>
									<TableHead className="w-20 font-semibold">
										{t("admin.users.points")}
									</TableHead>
									<TableHead className="w-32 font-semibold">
										{t("admin.users.joined")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredUsers.map((user) => (
									<TableRow
										key={user.id}
										className="hover:bg-gray-50 transition-colors"
									>
										<TableCell>
											<Avatar className="w-10 h-10">
												<AvatarImage src={user.image || ""} alt={user.name} />
												<AvatarFallback className="text-xs">
													{user.name
														? getInitials(user.name)
														: user.email.charAt(0).toUpperCase()}
												</AvatarFallback>
											</Avatar>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<span className="font-medium">{user.name}</span>
												{user.emailVerified && (
													<Badge
														variant="outline"
														className="text-green-600 text-xs px-1 py-0"
													>
														✓
													</Badge>
												)}
											</div>
										</TableCell>
										<TableCell className="text-muted-foreground">
											{user.email}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{user.phone || "-"}
										</TableCell>
										<TableCell>
											<Badge
												variant={user.isActive ? "default" : "secondary"}
												className="text-xs"
											>
												{user.isActive
													? t("admin.users.active")
													: t("admin.users.inactive")}
											</Badge>
										</TableCell>
										<TableCell className="text-center font-medium">
											{user.points || 0}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{formatDate(user.createdAt)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</div>
		</AdminLayout>
	);
}
